import React, { useEffect, useRef } from 'react';
import { spacing, useTheme } from '@fti/design-system';
import type { ColumnType } from '../../types/columnType';
import { COLUMN_TYPES } from '../../types/columnType';

interface TypeSelectorProps {
  currentType: ColumnType;
  onTypeChange: (newType: ColumnType) => void;
  onClose: () => void;
}

export const TypeSelector: React.FC<TypeSelectorProps> = ({
  currentType,
  onTypeChange,
  onClose,
}) => {
  const { theme } = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Select column type"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 1000,
        marginTop: '4px',
        backgroundColor: theme.colors.background.paper,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.md,
        boxShadow: theme.shadows.lg,
        minWidth: '140px',
        padding: spacing.xs,
      }}
    >
      {COLUMN_TYPES.map((type) => (
        <button
          key={type}
          type="button"
          role="option"
          aria-selected={type === currentType}
          onClick={() => onTypeChange(type)}
          style={{
            display: 'block',
            width: '100%',
            padding: `${spacing.xs} ${spacing.sm}`,
            border: 'none',
            borderRadius: theme.borderRadius.sm,
            backgroundColor: type === currentType
              ? `${theme.colors.primary[500] || theme.colors.primary[400]}20`
              : 'transparent',
            color: theme.colors.text.primary,
            fontSize: '0.8rem',
            fontWeight: type === currentType ? 600 : 400,
            textAlign: 'left',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            if (type !== currentType) {
              e.currentTarget.style.backgroundColor = theme.colors.background.elevated;
            }
          }}
          onMouseLeave={(e) => {
            if (type !== currentType) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {type}
        </button>
      ))}
    </div>
  );
};
